import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogBoundryService } from '@secca/core/services/dialog-boundry.service';

export type AtlasPromptLanguage = 'danish' | 'norwegian' | 'swedish' | 'finnish' | 'english';
export type AtlasPromptSet = 'general' | 'case';

export interface AtlasAdditionalPrompt {
  title: string;
  category: string;
  questions: Record<AtlasPromptLanguage, string>;
}

export interface AtlasAdditionalPromptsDialogData {
  prompts: AtlasAdditionalPrompt[];
  generalPrompts?: AtlasAdditionalPrompt[];
  casePrompts?: AtlasAdditionalPrompt[];
  defaultPromptSet?: AtlasPromptSet;
  defaultLanguage?: AtlasPromptLanguage;
}

export interface AtlasPromptSelection {
  prompt: string;
  sourceRect: {
    left: number;
    top: number;
    width: number;
    height: number;
  } | null;
}

@Component({
  selector: 'app-atlas-chatbot-additional-prompts-dialog',
  templateUrl: './atlas-chatbot-additional-prompts-dialog.component.html',
  styleUrls: ['./atlas-chatbot-additional-prompts-dialog.component.scss'],
  standalone: false
})
export class AtlasChatbotAdditionalPromptsDialogComponent {
  @Output() readonly promptSelected = new EventEmitter<AtlasPromptSelection>();
  readonly promptSetOptions: { value: AtlasPromptSet; label: string }[] = [
    { value: 'general', label: 'General' },
    { value: 'case', label: 'Case related' }
  ];
  readonly languageOptions: { value: AtlasPromptLanguage; label: string; code: string }[] = [
    { value: 'danish', label: 'Dansk', code: 'DK' },
    { value: 'norwegian', label: 'Norsk', code: 'NO' },
    { value: 'swedish', label: 'Svenska', code: 'SE' },
    { value: 'finnish', label: 'Suomi', code: 'FI' },
    { value: 'english', label: 'English', code: 'GB' }
  ];
  selectedPromptSet: AtlasPromptSet;
  selectedLanguage: AtlasPromptLanguage;
  selectedCategory = 'All';
  categoryOptions: { label: string; count: number }[] = [];
  promptGroups: { category: string; prompts: AtlasAdditionalPrompt[] }[] = [];
  private readonly generalPrompts: AtlasAdditionalPrompt[];
  private readonly casePrompts: AtlasAdditionalPrompt[];

  constructor(
    private dialogRef: MatDialogRef<AtlasChatbotAdditionalPromptsDialogComponent, string>,
    @Inject(MAT_DIALOG_DATA) public data: AtlasAdditionalPromptsDialogData,
    public dialogBoundryService: DialogBoundryService
  ) {
    this.generalPrompts = data?.generalPrompts ?? (data?.defaultPromptSet === 'general' ? data?.prompts ?? [] : []);
    this.casePrompts = data?.casePrompts ?? (data?.defaultPromptSet === 'case' || !data?.defaultPromptSet ? data?.prompts ?? [] : []);
    this.selectedPromptSet = data?.defaultPromptSet ?? 'general';
    this.selectedLanguage = data?.defaultLanguage ?? 'danish';
    this.categoryOptions = this.buildCategoryOptions();
    this.updatePromptGroups();
  }

  selectPrompt(prompt: AtlasAdditionalPrompt, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    const promptText = this.getPromptText(prompt);
    const sourceRect = this.getPromptSourceRect(event);
    this.promptSelected.emit({ prompt: promptText, sourceRect });
  }

  getPromptText(prompt: AtlasAdditionalPrompt): string {
    return prompt.questions[this.selectedLanguage] || prompt.questions.danish || '';
  }

  selectPromptSet(promptSet: AtlasPromptSet, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.selectedPromptSet === promptSet) {
      return;
    }

    this.selectedPromptSet = promptSet;
    this.selectedCategory = 'All';
    this.categoryOptions = this.buildCategoryOptions();
    this.updatePromptGroups();
  }

  selectCategory(category: string, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedCategory = category;
    this.updatePromptGroups();
  }

  trackByCategory(_: number, category: { label: string }): string {
    return category.label;
  }

  trackByPromptSet(_: number, promptSet: { value: AtlasPromptSet }): AtlasPromptSet {
    return promptSet.value;
  }

  trackByGroup(_: number, group: { category: string }): string {
    return group.category;
  }

  trackByPrompt(_: number, prompt: AtlasAdditionalPrompt): string {
    return `${prompt.category}:${prompt.questions.danish}`;
  }

  private buildCategoryOptions(): { label: string; count: number }[] {
    const prompts = this.getSelectedPrompts();
    const options: { label: string; count: number }[] = [{ label: 'All', count: prompts.length }];

    prompts.forEach(prompt => {
      const category = prompt.category || 'General';
      const existingCategory = options.find(option => option.label === category);
      if (existingCategory) {
        existingCategory.count++;
      } else {
        options.push({ label: category, count: 1 });
      }
    });

    return options;
  }

  private updatePromptGroups(): void {
    const groups: { category: string; prompts: AtlasAdditionalPrompt[] }[] = [];
    const selectedPrompts = this.getSelectedPrompts();
    const prompts = this.selectedCategory === 'All'
      ? selectedPrompts
      : selectedPrompts.filter(prompt => (prompt.category || 'General') === this.selectedCategory);

    prompts.forEach(prompt => {
      const category = prompt.category || 'General';
      const existingGroup = groups.find(group => group.category === category);
      if (existingGroup) {
        existingGroup.prompts.push(prompt);
      } else {
        groups.push({ category, prompts: [prompt] });
      }
    });

    this.promptGroups = groups;
  }

  private getSelectedPrompts(): AtlasAdditionalPrompt[] {
    return this.selectedPromptSet === 'case' ? this.casePrompts : this.generalPrompts;
  }

  private getPromptSourceRect(event?: MouseEvent): AtlasPromptSelection['sourceRect'] {
    const target = event?.currentTarget instanceof HTMLElement
      ? event.currentTarget
      : event?.target instanceof HTMLElement
        ? event.target.closest('.additional-prompt') as HTMLElement | null
        : null;

    if (!target) {
      return null;
    }

    const rect = target.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    };
  }

  close(): void {
    this.dialogRef.close();
  }
}
